import Big from 'big.js';
import { DateTime } from 'luxon';
import Batch from 'smg/batch';
import * as buttons from 'smg/buttons';
import { GetLineItems } from 'smg/customer-api/v2/lineitems';
import { OpenOrderInvoice } from 'smg/customer-api/v2/orders';
import * as models from 'smg/customer-api/models';
import { Orders } from 'smg/customer-api/orders';
import * as session from 'smg/customer-api/session';
import { ShowError } from 'smg/errors';
import { Picture } from 'smg/picture';
import * as template from 'smg/template';
import { DollarFormat } from 'smg/util';
import { AccountReady } from '../account-session';
import { BindClearSearch, PageLoader } from '../account';
import * as carts from '../carts';
import { Relref } from '../hugo';
import { OrganizationView, RenderOrganizationSwitch, ResolveOrganizationView } from '../organization-view';
import * as payments from '../payments';

void main();

async function main() {
    if (await AccountReady) {
        return;
    }

    const $empty = document.querySelector('#orders-empty') as HTMLElement;
    const $emptyTitle = document.querySelector('#orders-empty-title') as HTMLElement;
    const $emptyBody = document.querySelector('#orders-empty-body') as HTMLElement;
    const $emptyCta = document.querySelector('#orders-empty-cta') as HTMLElement;
    const $noMatch = document.querySelector('#orders-no-match') as HTMLElement;
    const $noMatchTitle = document.querySelector('#orders-no-match-title') as HTMLElement;
    const $noMatchBody = document.querySelector('#orders-no-match-body') as HTMLElement;

    BindClearSearch();

    let user: models.User;
    let view: OrganizationView;

    const b = new Batch();

    const carriers = b.get('/carriers', {
        params: {
            'data': 'name,link',
        },
    }).then(resp => {
        const data = models.CarriersHyejooResponse.fromJSON(resp.data);
        const m = new Map<number, models.Carrier>();
        for (const carrier of data.carriers) {
            m.set(carrier.id, carrier);
        }
        return m;
    });

    const lineItems = GetLineItems({ data: 'name,pluralName,product,shipping' });

    // `get` awaits this, but a failure while init is still running would
    // otherwise reject with no handler attached yet.
    const lookups = Promise.all([carriers, lineItems, b.exec()]);
    void lookups.catch(() => undefined);

    const pl: PageLoader<models.HyejooOrder> = new PageLoader<models.HyejooOrder>({
        cards: { empty: $empty, noMatch: $noMatch },
        init: async () => {
            user = (await session.Session()).user;
            view = await ResolveOrganizationView(user);
            RenderOrganizationSwitch(view, user);
        },
        get: async () => {
            const [data] = await Promise.all([Orders({
                sort: pl.sort(),
                max: pl.currentPageSize(),
                data: 'invoiceNumber,created,orderLineItems,orderProofs,orderShippingOptions,total,paid,balance,totalSansTax,orderStatusID,userID',
                p: pl.currentPage(),
                q: pl.search(),
            }, view.params()), lookups]);
            return { rows: data.results, nextPage: data.nextPage };
        },
        draw: async order => {
            const el = template.Clone('#order-template').firstElementChild as HTMLElement;

            const invoiceNumber = el.querySelector('.order-invoice-number') as HTMLElement;
            invoiceNumber.textContent = String(order.invoiceNumber);

            const date = el.querySelector('.order-date') as HTMLElement;
            if (order.created.isValid) {
                date.textContent = order.created.toLocaleString(DateTime.DATE_SHORT);
            }

            const total = order.total;
            const totalSansTax = order.totalSansTax;
            let shipping = Big(0);
            for (const ol of order.orderLineItems) {
                if (!ol.lineItemID) {
                    continue;
                }

                const lineItem = (await lineItems).get(ol.lineItemID);
                if (!lineItem?.shipping) {
                    continue;
                }

                shipping = shipping.add(ol.price.mul(ol.quantity));
            }
            const balance = order.balance;

            const tax = total.sub(totalSansTax);
            const subtotal = totalSansTax.sub(shipping);

            const subtotalEls = el.querySelectorAll('.order-subtotal');
            subtotalEls.forEach(el => el.textContent = DollarFormat(subtotal));

            const shippingEls = el.querySelectorAll('.order-shipping');
            shippingEls.forEach(el => el.textContent = DollarFormat(shipping));

            const taxEls = el.querySelectorAll('.order-tax');
            taxEls.forEach(el => el.textContent = DollarFormat(tax));

            const totalEls = el.querySelectorAll('.order-total');
            totalEls.forEach(el => el.textContent = DollarFormat(total));

            const balanceEls = el.querySelectorAll('.order-balance');
            balanceEls.forEach(el => el.textContent = DollarFormat(balance));

            const balanceContainers = el.querySelectorAll('.order-balance-container');
            const makePayment = el.querySelector('.order-make-payment') as HTMLElement;
            const reorder = el.querySelector('.order-reorder') as HTMLButtonElement;

            if (view.active) {
                const $attributionRow = el.querySelector('.order-attribution-row') as HTMLElement;
                const $attribution = el.querySelector('.organization-attribution') as HTMLElement;
                view.fillAttribution($attribution, order.userID);
                $attributionRow.classList.remove('d-none');
            }

            if (balance.gt('0.05')) {
                balanceContainers.forEach(el => el.classList.add('text-danger'));
            }

            if (balance.gt('0.05')) {
                makePayment.addEventListener('click', () => payments.PromptPayment(user, [order.id], balance, false).then(() => pl.load()));
            } else {
                makePayment.parentElement?.removeChild(makePayment);
            }

            const orderDetails = el.querySelector('.order-details-btn') as HTMLAnchorElement;
            orderDetails.href = view.withLens(`${Relref('/orders/track')}?id=${order.id}`);

            // $0 reorders are rejected server-side (#7049), so drop the button instead of wiring a dead click handler
            if (total.eq(0)) {
                reorder.parentElement?.removeChild(reorder);
            } else {
                reorder.addEventListener('click', async () => {
                    buttons.Start(reorder);
                    await carts.AddCartItem({ orderID: order.id }, view.cartOptions()).then(() => {
                        reorder.classList.remove('btn-primary', 'btn-success', 'text-white');
                        reorder.classList.add('btn-outline-success');

                        reorder.querySelector('i')?.classList.remove('bi-cart-fill');
                        reorder.querySelector('i')?.classList.add('bi-check-lg');
                    }).finally(() => buttons.Stop(reorder));
                });
            }

            const invoice = el.querySelector('.order-invoice') as HTMLButtonElement;
            invoice.addEventListener('click', () => {
                buttons.Start(invoice);
                void OpenOrderInvoice(`id:${order.id ?? ''}`, view.params().organization).catch(ShowError).finally(() => buttons.Stop(invoice));
            });

            const proofsContainer = el.querySelector('.order-proofs-container') as HTMLElement;
            const proofsParent = el.querySelector('.order-proofs-parent') as HTMLElement;
            if (order.orderProofs.length) {
                for (const op of order.orderProofs) {
                    const p = op.proof;
                    if (!p) {
                        continue;
                    }

                    const urls = p.getURLs();

                    const a = template.Clone('#order-proof-template').firstElementChild as HTMLAnchorElement;
                    a.href = urls.main;
                    a.setAttribute('data-fancybox', order.id);

                    const img = a.querySelector('img') as HTMLImageElement;
                    img.alt = op.name;
                    if (urls.main) {
                        void Picture(img, urls.main, {
                            displayWidth: 120,
                            alt: op.name,
                            fancyboxThumb: true,
                            pdfSrc: p.url() || undefined,
                        });
                    } else {
                        img.src = urls.thumbnail;
                    }

                    proofsParent.appendChild(a);
                }
            } else {
                proofsContainer.parentElement?.removeChild(proofsContainer);
            }

            let shipmentsCount = 0;
            let delivered = false;
            const trackingParent = el.querySelector('.order-tracking-parent') as HTMLElement;
            for (const orderShippingOption of order.orderShippingOptions) {
                for (const orderShippingOptionShipment of orderShippingOption.orderShippingOptionShipments) {
                    const shipment = orderShippingOptionShipment.shipment;
                    if (!shipment) {
                        continue;
                    }

                    if (shipment.deliveredAt.isValid) {
                        delivered = true;
                    }

                    if (shipmentsCount++) {
                        trackingParent.appendChild(document.createTextNode(', '));
                    }

                    const carrier = (await carriers).get(shipment.carrierID);

                    if (carrier) {
                        const a = document.createElement('a');
                        a.href = carrier.trackingURL(shipment.trackingNumber);
                        a.textContent = shipment.trackingNumber;

                        trackingParent.appendChild(a);
                    } else {
                        trackingParent.appendChild(document.createTextNode(shipment.trackingNumber));
                    }
                }
            }
            if (!shipmentsCount) {
                const trackingContainer = el.querySelector('.order-tracking-container');
                trackingContainer?.parentElement?.removeChild(trackingContainer);
            }

            const orderLineItemStages: models.OrderLineItemStage[] = [];

            if (order.orderLineItems.length) {
                const qtys: Record<string, number> = {};
                for (const ol of order.orderLineItems) {
                    if (ol.orderLineItemStages.length) {
                        orderLineItemStages.push(...ol.orderLineItemStages);
                    }

                    if (!ol.quantity) {
                        continue;
                    }

                    const lineItem = (await lineItems).get(ol.lineItemID);

                    if (!(ol.lineItem || lineItem?.product)) {
                        continue;
                    }

                    const name = ol.lineItem || lineItem?.pluralName || lineItem?.name || 'Other';
                    qtys[name] = (qtys[name] || 0) + ol.quantity;
                }

                const summary = el.querySelector('.order-summary') as HTMLElement;
                summary.textContent = Object.keys(qtys).map(n => `${qtys[n]} ${n}`).join(', ');
            }

            const orderStatusEl = el.querySelector('.order-status') as HTMLElement;
            let orderStatus: string;
            switch (true) {
            case delivered:
                orderStatus = 'Delivered';
                orderStatusEl.textContent = orderStatus;
                break;
            case shipmentsCount !== 0:
                orderStatus = 'Shipped';
                orderStatusEl.textContent = orderStatus;
                break;
            case orderLineItemStages.length !== 0:
                orderLineItemStages.sort((a, b) => b.created.toMillis() - a.created.toMillis());
                orderStatus = orderLineItemStages[0].stage?.name ?? '';
                orderStatusEl.textContent = orderStatus;
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_CONFIRMED:
                orderStatus = 'In production';
                orderStatusEl.textContent = orderStatus;
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_UNCONFIRMED:
                orderStatus = 'Preparing for production';
                orderStatusEl.textContent = orderStatus;
                break;
            case order.orderStatusID === models.OrderStatusID.ORDER_STATUSES_ORDER:
                orderStatus = 'Order received';
                orderStatusEl.textContent = orderStatus;
                break;
            }

            return el;
        },
        afterLoad: state => {
            // A failed init settles before `view` exists, so read it only
            // in the states a resolved view reaches.
            if (state === 'empty' && view.active) {
                const orgName = view.organization?.name ?? '';
                $emptyTitle.textContent = `No orders from ${orgName} yet`;
                $emptyBody.textContent = 'This list fills in as members place orders.';
                $emptyCta.classList.add('d-none');
                return;
            }

            if (state === 'no-match') {
                const orgName = view.organization?.name ?? '';
                const search = pl.search().trim();
                if (view.active) {
                    $noMatchTitle.textContent = `Nothing in ${orgName} matches "${search}"`;
                    $noMatchBody.textContent = `Clear the search to see every order in ${orgName}.`;
                } else {
                    $noMatchTitle.textContent = `No orders match "${search}".`;
                }
            }
        },
    });

    pl.load();
}
